import * as log from "../utils/log";
import { Loader } from "../loading/Loader";
import { MediaEvents } from "../players/MediaEvents";

/**
 * Player for video files.
 * {@link https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement|Reference to media API}
 *
 * @emits VideoPlayerAlpha#play
 * @emits VideoPlayerAlpha#pause
 * @emits VideoPlayerAlpha#ended
 * @emits VideoPlayerAlpha#error
 *
 * @example
 * const queue = new LoadingQueue();
 * const videoLoader = new VideoLoader('videoFile.mp4', cacheTimestamp, 'videos/');
 * queue.addLoader(videoLoader);
 * queue.addEventListener(LoaderEvent.COMPLETE, () => {
 *      const video = new VideoPlayer('videoFile.mp4').play();
 *      view.addChild(video);
 * }, this);
 * queue.addEventListener(LoaderEvent.ERROR, () => {
 *      throw Error('Unable to load video')
 * }, this);
 * queue.load();
 **/
@log.decorator
export class VideoPlayer extends PIXI.Sprite {

    log;
    warn;
    error;

    protected _video;
    protected _isVideoReady: boolean;
    protected _isPlaying: boolean;
    protected _width: number;
    protected _height: number;
    protected _frameProcessInterval: number;
    protected _frameProcessIntervalMs: number;
    protected _onPlay: Function;
    protected _onPause: Function;
    protected _onError: Function;
    protected _onTimeUpdate: Function;

    protected _outputCanvas;
    protected _outputCanvasContext;
    protected _outputTexture;

    constructor(protected loaderId: string,
                loop: boolean = false,
                autoPlay: boolean = false,
                muted: boolean = true,
                volume: number = 1,
                fps: number = 30) {
        super();

        this.log('Initializing...');
        this.attachEvents();
        this.loadVideo();
        this.createElements();

        this.fps = fps;
        this.loop = loop;
        this.muted = true;
        this.volume = volume;

        if (autoPlay) {
            this.play();
        }
    }

    public play() {
        
        this.attachEvents();
        this.log('Requesting playback, current time[%s]...', this.currentTime);
        this._isVideoReady = false;
        this._video.play();
        return this;
    }

    public pause() {
        this.log('Requesting pause...');
        this._video.pause();
        return this;
    }

    public destroy() {
        this.log('Destroying...');
        this._video.currentTime = 0
        this._pause(true);
        this.detachEvents();

        if (this.parent) {
            this.parent.removeChild(this);
        }

        if (this._video) {
            this._video = null;
        }

        this._outputTexture = null;
        this._outputCanvas = null;
        this._outputCanvasContext = null;

        super.destroy(true);
    }

    set fps(value: number) {
        this._frameProcessIntervalMs = 1000 / value;
    }

    get fps(): number {
        return 1000 / this._frameProcessIntervalMs;
    }

    set loop(value: boolean) {
        this._video.loop = value;
    }

    get loop(): boolean {
        return this._video.loop;
    }

    set muted(value: boolean) {
        this._video.muted = value;
    }

    get muted(): boolean {
        return this._video.muted;
    }

    set volume(value: number) {
        this._video.volume = value;
    }

    get volume(): number {
        return this._video.volume;
    }

    get duration(): number {
        return this._video.duration;
    }

    get currentTime(): number {
        return this._video.currentTime;
    }

    set currentTime(value: number) {
        this._video.currentTime = value;
    }

    get ended(): boolean {
        return this._video.ended;
    }

    set width(value: number) {
        this.warn('Unable to change width of video. Use scale instead.');
    }

    get width(): number {
        return this._width;
    }

    set height(value: number) {
        this.warn('Unable to change height of video. Use scale instead.');
    }

    get height(): number {
        return this._height;
    }

    get paused(): boolean {
        return this._video.paused;
    }

    protected createElements() {
        // Not using this because it is not stable
        // this.texture = PIXI.Texture.fromVideo(this._video);

        this._outputCanvas = document.createElement("canvas");
        this._outputCanvas.width = this._width
        this._outputCanvas.height = this._height
        this._outputTexture = new PIXI.BaseTexture(this._outputCanvas);
        this.texture = new PIXI.Texture(this._outputTexture);
        this._outputCanvasContext = this._outputCanvas.getContext('2d');
    }

    protected setResolution() {
        this._width = this._video.videoWidth;
        this._height = this._video.videoHeight;
    }

    protected loadVideo() {
        this._video = Loader.getLoader(this.loaderId).getData();

        this.setResolution();

        this.log(
            'Video resolution[%sx%s] container resolution[%sx%s]',
            this._video.videoWidth,
            this._video.videoHeight,
            this._width,
            this._height
        );
           
        this._onPlay = () => this.onPlay();
        this._onTimeUpdate = () => this.onTimeUpdate();
        this._onPause = () => this.onPause();
        this._onError = () => this.onError();
    }

    protected onTimeUpdate() {
        this.log('Time update current time[%s]...', this.currentTime);
        
        if(this._video.currentTime === this._video.duration) {
            this.detachEvents()
            this._pause(true)
            this.emit(MediaEvents.ended);
        } else if(!this._isPlaying && this.currentTime > 0) {
            this._isVideoReady = true;
            this._play();
            this.emit(MediaEvents.videoReady);
        }
    }

    protected processFrame() {
        // Not using this because not stable
        // this.texture.update();

        this._outputCanvasContext.drawImage(this._video, 0, 0);
        this._outputTexture.update();
    }

    protected onPlay() {
        this.log(
            'Playback started from[%s]sec fps[%s] loop[%s] volume[%s] muted[%s]',
            this.currentTime,
            this.fps,
            this.loop,
            this.volume,
            this.muted
        );

        if (this._isVideoReady) {
            this._play();
        }

        this.emit(MediaEvents.play);
    }

    protected onPause() {
        this.log('Playback paused at[%s]sec', this.currentTime);
        this._pause(true);
        this.emit(MediaEvents.pause);
    }

    protected onError() {
        this.error('Playback error code[%s] message', this._video.error.code, this._video.error.message);
        this.emit(MediaEvents.error, this._video.error);
    }

    protected attachEvents() {
        if (this._video) {
            this.log('Attaching events...');
            this._video.addEventListener('play', this._onPlay);
            this._video.addEventListener('timeupdate', this._onTimeUpdate);
            this._video.addEventListener('pause', this._onPause);
            this._video.addEventListener('error', this._onError);
        } else {
            this.warn('There is no video to attach event to');
        }
    }

    protected detachEvents() {
        if (this._video) {
            this.log('Detaching events...');
            this._video.removeEventListener('play', this._onPlay);
            this._video.removeEventListener('timeupdate', this._onTimeUpdate);
            this._video.removeEventListener('pause', this._onPause);
            this._video.removeEventListener('error', this._onError);
        } else {
            this.warn('There is no video to detach event from');
        }
    }

    protected _play() {
        if (this._isPlaying) {
            return this.warn('Already playing current time[%s]...', this.currentTime);
        }

        this.log('Starting processing frames current time[%s]...', this.currentTime);
        this._isPlaying = true;

        this.processFrame();
        // @ts-ignore
        this._frameProcessInterval = setInterval(() => this.processFrame(), this._frameProcessIntervalMs);
    }

    protected _pause(processLastFrame: boolean = false) {
        if (!this._isPlaying) {
            return this.warn('Already paused current time[%s]...', this.currentTime);
        }

        if (processLastFrame) {
            this.processFrame();
        }

        this.log('Stopped processing frames current time[%s]...', this.currentTime);
        this._isPlaying = false;
        clearInterval(this._frameProcessInterval);
    }

    toString() {
        return this.constructor['name'] + ' ' + this.loaderId;
    }
}
